import { nanoid } from "nanoid";

import type { PostCompanyReviewFormRequest } from "@performyard/service-contracts";

import type { FormQuestionSuggestion, FormSuggestion } from "../types";

type RequestQuestions = NonNullable<PostCompanyReviewFormRequest["questions"]>;
type RequestQuestion = RequestQuestions[number];

type RatingScaleOption = { value: number | ""; name: string; description?: string };

/** Description is optional on the AI schema, but the Tornado form templates
 *  subscript `c['description']` (form_body.html:249, goal-discussion.html:127),
 *  which KeyErrors with HTTP 500 when the field is missing. Default to "" so
 *  AI-suggested forms render the same way the legacy form-builder's do (it
 *  always seeds `description: ""` — see legacyRatingScale.tsx). */
const ensureDescriptionDefault = <T extends RatingScaleOption>(opt: T): T => ({
	...opt,
	description: opt.description ?? "",
});

/**
 * Convert an AI-suggested form's questions into the API request shape expected
 * by `POST /v1/company/reviews/forms`. Generates a fresh refId per question
 * (the API requires a unique string ref so calculator sub-questions can resolve).
 */
const transformQuestion = (q: FormQuestionSuggestion): RequestQuestion => {
	const base = {
		refId: nanoid(),
		type: q.type,
		question: q.question,
		subtext: q.subtext,
		is_required: q.is_required,
		is_hidden: false,
	} as Partial<RequestQuestion> & { refId: string; type: FormQuestionSuggestion["type"]; question: string };

	if (q.type === "rating-scale") {
		// Rating-scale uses RatingScaleOption[] in the `choices` field
		return { ...base, choices: (q.rating_scale ?? []).map(ensureDescriptionDefault) } as RequestQuestion;
	}

	if (q.type === "multiple-choice" || q.type === "multiple-select") {
		return { ...base, choices: q.choices ?? [] } as RequestQuestion;
	}

	if (q.type === "numeric") {
		return {
			...base,
			min: q.min,
			max: q.max,
			decimals: q.decimals,
		} as RequestQuestion;
	}

	if (q.type === "goal-discussion") {
		return {
			...base,
			add_scaling: (q.add_scaling ?? []).map(ensureDescriptionDefault),
		} as RequestQuestion;
	}

	// text, multiline, date, goal-creation, instructions, calculator — no extra fields
	return base as RequestQuestion;
};

export const transformFormSuggestionToRequest = (suggestion: FormSuggestion): PostCompanyReviewFormRequest => ({
	name: suggestion.name,
	questions: suggestion.questions.map(transformQuestion),
});
